export interface ValidationResult {
  isValid: boolean;
  issues: string[];
}

export interface NodeValidationResult {
  nodeId: string;
  identifier?: string;
  validation: ValidationResult;
  hasDuplicateIdentifier: boolean;
}

// Helper function to check if operator needs a value input
export const operatorNeedsValue = (operator: string): boolean => {
  return !["is_true", "is_false", "is_null", "is_not_null"].includes(operator);
};

export interface FlowValidationResult {
  isValid: boolean;
  invalidNodes: NodeValidationResult[];
  allIssues: string[];
}

// Validate a single node (extracted from CustomNode.tsx)
export const validateNode = (data: any, placeholders: any, globalRegistry: any, decisionVariablesRegistry?: any): ValidationResult => {
  const issues: string[] = [];
  
  // Skip validation for placeholder nodes
  if (data.isPlaceholder) {
    return { isValid: true, issues: [] };
  }
  
  // Decision nodes have different validation rules
  if (data.entityType === "DECISION") {
    // Check if decision data exists
    if (!data.decisionData) {
      issues.push("Decision node has no decision logic configured");
    } else if (Array.isArray(data.decisionData.conditions)) {
      // New model: conditions array
      if (data.decisionData.conditions.length === 0) {
        issues.push("Decision node has no conditions configured");
      } else {
        data.decisionData.conditions.forEach((cond: any, idx: number) => {
          if (!cond.variable || cond.variable.trim() === "") {
            issues.push(`Condition ${idx + 1} is missing a variable`);
          } else {
            // Check if the variable is valid (exists in decisionVariablesRegistry or is a persistent placeholder)
            const isValidDecisionVariable = decisionVariablesRegistry && decisionVariablesRegistry[cond.variable] !== undefined;
            const isPersistentPlaceholder = globalRegistry && globalRegistry[cond.variable]?.persist === true;
            
            if (!isValidDecisionVariable && !isPersistentPlaceholder) {
              issues.push(`Condition ${idx + 1} uses invalid variable "${cond.variable}" (not a decision variable or persistent placeholder)`);
            }
          }
          // Only check for missing value if the operator actually needs one
          if (operatorNeedsValue(cond.operator) && (cond.value === undefined || cond.value === null || (typeof cond.value === "string" && cond.value.trim() === ""))) {
            issues.push(`Condition ${idx + 1} is missing a comparison value`);
          }
        });
      }
      // Check if at least one path is configured
      if (!data.decisionData.truePath && !data.decisionData.falsePath) {
        issues.push("Decision node has no paths configured");
      }
    } else if (data.decisionData.condition) {
      // Old model: single condition
      if (!data.decisionData.condition.variable || data.decisionData.condition.variable.trim() === "") {
        issues.push("Decision node is missing a variable");
      } else {
        // Check if the variable is valid (exists in decisionVariablesRegistry or is a persistent placeholder)
        const isValidDecisionVariable = decisionVariablesRegistry && decisionVariablesRegistry[data.decisionData.condition.variable] !== undefined;
        const isPersistentPlaceholder = globalRegistry && globalRegistry[data.decisionData.condition.variable]?.persist === true;
        
        if (!isValidDecisionVariable && !isPersistentPlaceholder) {
          issues.push(`Decision node uses invalid variable "${data.decisionData.condition.variable}" (not a decision variable or persistent placeholder)`);
        }
      }
      // Only check for missing value if the operator actually needs one
      if (operatorNeedsValue(data.decisionData.condition.operator) && 
          (data.decisionData.condition.value === undefined || data.decisionData.condition.value === null || 
           (typeof data.decisionData.condition.value === "string" && data.decisionData.condition.value.trim() === ""))) {
        issues.push("Decision node is missing a comparison value");
      }
      if (!data.decisionData.truePath && !data.decisionData.falsePath) {
        issues.push("Decision node has no paths configured");
      }
    }
    return { isValid: issues.length === 0, issues };
  }
  
  // Check for empty content - but allow empty content for BOT nodes with tool calls
  const hasValidToolCalls = data.entityType === "BOT" && data.toolCalls && data.toolCalls.length > 0 && data.toolCalls.some((tc: any) => tc.name && tc.name.trim());
  if ((!data.content || data.content.trim() === "") && !hasValidToolCalls) {
    issues.push("Node content is empty");
  }
  
  // Check for undefined placeholders in content
  if (data.content) {
    const placeholderRegex = /\{([^}]+)\}/g;
    let match;
    while ((match = placeholderRegex.exec(data.content)) !== null) {
      const placeholderName = match[1];
      const hasLocalDefinition = placeholders[placeholderName];
      const hasGlobalDefinition = globalRegistry && globalRegistry[placeholderName];
      const hasDecisionVariable = decisionVariablesRegistry && decisionVariablesRegistry[placeholderName];
      
      if (!hasLocalDefinition && !hasGlobalDefinition && !hasDecisionVariable) {
        issues.push(`Placeholder "${placeholderName}" is not defined`);
      } else {
        // Only conversation placeholders need examples - decision variables don't need examples since they can't have examples set
        // Check if placeholder has examples - prioritize global registry
        const hasGlobalDefinition = globalRegistry[placeholderName];
        let definition;
        if (hasGlobalDefinition && hasGlobalDefinition.values && hasGlobalDefinition.values.length > 0) {
          // Use global definition if it exists and has examples
          definition = hasGlobalDefinition;
        } else if (hasGlobalDefinition) {
          // Use global definition even if it has no examples (to show the warning)
          definition = hasGlobalDefinition;
        } else {
          definition = placeholders[placeholderName];
        }
        
        if (definition && (!definition.values || definition.values.length === 0)) {
          issues.push(`Placeholder "${placeholderName}" has no examples`);
        }
      }
    }
  }
  
  return { isValid: issues.length === 0, issues };
};

// Validate interjection placeholders
export const validateInterjectionPlaceholders = (
  interjectionGroups: any[],
  globalRegistry: any
): string[] => {
  const issues: string[] = [];
  
  if (!interjectionGroups || interjectionGroups.length === 0) {
    return issues;
  }
  
  interjectionGroups.forEach(group => {
    if (!group.content || !Array.isArray(group.content)) {
      return;
    }
    
    group.content.forEach((content: any, contentIndex: number) => {
      // Check interjection_text for placeholders
      if (content.interjection_text) {
        const placeholderRegex = /\{([^}]+)\}/g;
        let match;
        while ((match = placeholderRegex.exec(content.interjection_text)) !== null) {
          const placeholderName = match[1];
          const hasPlaceholderDefinition = globalRegistry && globalRegistry[placeholderName];
          
          if (!hasPlaceholderDefinition) {
            issues.push(`Interjection group "${group.groupName || group.id}" content ${contentIndex + 1}: Placeholder "${placeholderName}" is not defined`);
          } else {
            // Check if placeholder has examples
            const definition = hasPlaceholderDefinition;
            if (definition && (!definition.values || definition.values.length === 0)) {
              issues.push(`Interjection group "${group.groupName || group.id}" content ${contentIndex + 1}: Placeholder "${placeholderName}" has no examples`);
            }
          }
        }
      }
      
      // Check interjection_response for placeholders
      if (content.interjection_response) {
        const placeholderRegex = /\{([^}]+)\}/g;
        let match;
        while ((match = placeholderRegex.exec(content.interjection_response)) !== null) {
          const placeholderName = match[1];
          const hasPlaceholderDefinition = globalRegistry && globalRegistry[placeholderName];
          
          if (!hasPlaceholderDefinition) {
            issues.push(`Interjection "${group.groupName || group.id}" content ${contentIndex + 1}: Placeholder "${placeholderName}" is not defined`);
          } else {
            // Check if placeholder has examples
            const definition = hasPlaceholderDefinition;
            if (definition && (!definition.values || definition.values.length === 0)) {
              issues.push(`Interjection "${group.groupName || group.id}" content ${contentIndex + 1}: Placeholder "${placeholderName}" has no examples`);
            }
          }
        }
      }
    });
  });
  
  return issues;
};

// Validate all nodes in the flow and return detailed results
export const validateFlow = (
  nodes: any[], 
  edges: any[],
  globalVariableRegistry: any,
  decisionVariablesRegistry?: any,
  interjectionGroups?: any[]
): FlowValidationResult => {
  const invalidNodes: NodeValidationResult[] = [];
  const allIssues: string[] = [];
  const placeholderIssues = new Set<string>(); // Track unique placeholder issues

  // Create a map to track duplicate identifiers
  const identifierMap: Record<string, string[]> = {};
  
  // First pass: collect all identifiers
  nodes.forEach(node => {
    if (!node.data.isPlaceholder && node.data.identifier) {
      const identifier = node.data.identifier;
      if (!identifierMap[identifier]) {
        identifierMap[identifier] = [];
      }
      identifierMap[identifier].push(node.id);
    }
  });

  // Build a map of decision node children
  const decisionNodeChildren = new Set<string>();
  
  // Check decision node paths
  nodes.forEach(node => {
    if (node.data.entityType === "DECISION" && node.data.decisionData) {
      const { truePath, falsePath } = node.data.decisionData;
      if (truePath) {
        const trueNode = nodes.find(n => n.data.identifier === truePath || n.id === truePath);
        if (trueNode) {
          decisionNodeChildren.add(trueNode.id);
        }
      }
      if (falsePath) {
        const falseNode = nodes.find(n => n.data.identifier === falsePath || n.id === falsePath);
        if (falseNode) {
          decisionNodeChildren.add(falseNode.id);
        }
      }
    }
  });
  
  // Also check edges for direct connections from decision nodes
  edges.forEach(edge => {
    const sourceNode = nodes.find(n => n.id === edge.source);
    if (sourceNode && sourceNode.data.entityType === "DECISION") {
      decisionNodeChildren.add(edge.target);
    }
  });

  // Second pass: validate each node
  nodes.forEach(node => {
    const placeholders = node.data.placeholders || {};
    const validation = validateNode(node.data, placeholders, globalVariableRegistry, decisionVariablesRegistry);
    
    // Check for duplicate identifier
    const hasDuplicateIdentifier = node.data.identifier && 
      identifierMap[node.data.identifier] && 
      identifierMap[node.data.identifier].length > 1;

    // Check if this node is a child of a decision node and lacks an identifier
    const isDecisionChild = decisionNodeChildren.has(node.id);
    const missingRequiredIdentifier = isDecisionChild && (!node.data.identifier || node.data.identifier.trim() === "");

    // If node is invalid or has duplicate identifier or missing required identifier, add to invalid nodes
    if (!validation.isValid || hasDuplicateIdentifier || missingRequiredIdentifier) {
      // Separate placeholder issues from other node issues
      const nodeSpecificIssues: string[] = [];
      const nodePlaceholderIssues: string[] = [];

      validation.issues.forEach(issue => {
        if (issue.includes("Placeholder \"") && issue.includes("has no examples")) {
          // This is a placeholder issue - add to global set
          placeholderIssues.add(issue);
          nodePlaceholderIssues.push(issue);
        } else if (issue.includes("Placeholder \"") && issue.includes("is not defined")) {
          // This is also a placeholder issue - add to global set
          placeholderIssues.add(issue);
          nodePlaceholderIssues.push(issue);
        } else {
          // This is a node-specific issue
          nodeSpecificIssues.push(issue);
        }
      });

      // Add duplicate identifier issue if present
      if (hasDuplicateIdentifier) {
        nodeSpecificIssues.unshift(`Duplicate identifier "${node.data.identifier}" is used by ${identifierMap[node.data.identifier].length} nodes`);
      }

      // Add missing required identifier issue if present
      if (missingRequiredIdentifier) {
        nodeSpecificIssues.unshift("All direct children of decision nodes must have identifiers");
      }

      // Only add to invalid nodes if there are node-specific issues or placeholder issues affecting this node
      if (nodeSpecificIssues.length > 0 || nodePlaceholderIssues.length > 0) {
        const nodeValidationResult: NodeValidationResult = {
          nodeId: node.id,
          identifier: node.data.identifier,
          validation: {
            isValid: false,
            issues: [...nodeSpecificIssues, ...nodePlaceholderIssues]
          },
          hasDuplicateIdentifier: !!hasDuplicateIdentifier
        };

        invalidNodes.push(nodeValidationResult);
        
        // Add node-specific issues to allIssues with node context
        nodeSpecificIssues.forEach(issue => {
          const nodeLabel = node.data.identifier ? `"${node.data.identifier}"` : `Node ${node.id}`;
          allIssues.push(`${nodeLabel}: ${issue}`);
        });
      }
    }
  });

  // Add unique placeholder issues to allIssues (without node context since they're global)
  placeholderIssues.forEach(issue => {
    allIssues.push(issue);
  });

  // Validate interjection placeholders if provided
  let interjectionIssues: string[] = [];
  if (interjectionGroups && interjectionGroups.length > 0) {
    interjectionIssues = validateInterjectionPlaceholders(
      interjectionGroups,
      globalVariableRegistry
    );
    interjectionIssues.forEach(issue => {
      allIssues.push(issue);
    });
  }

  return {
    isValid: invalidNodes.length === 0 && placeholderIssues.size === 0 && interjectionIssues.length === 0,
    invalidNodes,
    allIssues
  };
}; 